/**
 * The product-event tracker: the one way a client emits a catalogued event.
 *
 * Every screen spec in docs/04-screen-specs.md names the events it emits, and for a
 * long time nothing emitted any of them. The pipeline is deliberately small, because
 * the ways an analytics client ruins a product are well known and all of them are
 * avoidable:
 *
 * - **It never throws into the caller.** A `track` call sits in a tap handler. If
 *   telemetry can throw there, telemetry can break the button.
 * - **It never blocks the interaction.** Events are queued and flushed on a timer, on
 *   a full batch, and when the app goes away.
 * - **It survives being offline.** The queue is persisted, so a lesson finished on a
 *   plane is still counted. It is also bounded, so a month offline cannot fill the
 *   device.
 * - **It stops when the learner opts out.** Not "stops sending" — stops *queueing*, and
 *   drops what it already has.
 *
 * The payload type comes from the catalogue, so a missing or misspelled field is a
 * compile error here rather than a row nobody can interpret later.
 */

import type { AnalyticsEventName, AnalyticsPayload, AnalyticsValue } from '@saysooner/contracts';
import { clientUlid } from './index.js';

export interface QueuedEvent {
  readonly id: string;
  readonly name: string;
  readonly at: number;
  readonly payload: Record<string, AnalyticsValue>;
}

/**
 * Where the queue survives a restart. A web app passes one backed by `localStorage`;
 * the mobile apps have their own implementations in Swift and Kotlin.
 */
export interface EventStore {
  load(): readonly QueuedEvent[];
  save(events: readonly QueuedEvent[]): void;
}

/** Keeps nothing. The default, so a caller that forgets a store still works. */
export class MemoryEventStore implements EventStore {
  private events: readonly QueuedEvent[] = [];
  load(): readonly QueuedEvent[] {
    return this.events;
  }
  save(events: readonly QueuedEvent[]): void {
    this.events = events;
  }
}

/** `localStorage`, degrading to memory where it is unavailable or full. */
export class LocalEventStore implements EventStore {
  constructor(private readonly key = 'vl.events') {}

  load(): readonly QueuedEvent[] {
    try {
      const raw = globalThis.localStorage?.getItem(this.key);
      if (raw === null || raw === undefined) return [];
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as QueuedEvent[]) : [];
    } catch {
      // Private browsing, cleared site data, a quota error, or a half-written value.
      // An unreadable queue is an empty queue; it is not worth a thrown error.
      return [];
    }
  }

  save(events: readonly QueuedEvent[]): void {
    try {
      globalThis.localStorage?.setItem(this.key, JSON.stringify(events));
    } catch {
      // Full or unavailable. The in-memory queue still flushes this session.
    }
  }
}

/**
 * The one call the tracker makes. Narrower than the whole client on purpose: the
 * tracker has no business being able to start a lesson, and a test fake should not have
 * to implement a generic route method to stand in for one endpoint.
 *
 * `SaySoonerClient` satisfies this structurally, and a type-level assertion in the test
 * suite keeps that true.
 */
export interface EventSink {
  call(
    name: 'trackEvents',
    input: { body: { events: QueuedEvent[] } },
    options: { idempotencyKey: string },
  ): Promise<{
    readonly accepted: number;
    readonly discarded: number;
    readonly rejectedNames: readonly string[];
  }>;
}

export interface TrackerOptions {
  readonly client: EventSink;
  readonly store?: EventStore;
  /** Flush when the queue reaches this many. The contract caps a batch at 50. */
  readonly batchSize?: number;
  /** Flush this often while events are waiting. */
  readonly flushIntervalMs?: number;
  /**
   * The hard ceiling on the persisted queue. Past it the *oldest* events are dropped:
   * a learner who has been offline for a month should still have this week counted.
   */
  readonly maxQueued?: number;
  readonly now?: () => number;
  /** Called when a flush fails, for diagnostics. Never used for control flow. */
  readonly onError?: (error: unknown) => void;
}

const DEFAULT_BATCH = 25;
const DEFAULT_INTERVAL_MS = 15_000;
const DEFAULT_MAX_QUEUED = 500;
/** The contract's own cap. A batch larger than this is a 400. */
const CONTRACT_MAX_BATCH = 50;

export class EventTracker {
  private queue: QueuedEvent[];
  private readonly store: EventStore;
  private readonly batchSize: number;
  private readonly flushIntervalMs: number;
  private readonly maxQueued: number;
  private readonly now: () => number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing: Promise<void> | null = null;
  private optedOut = false;

  constructor(private readonly options: TrackerOptions) {
    this.store = options.store ?? new MemoryEventStore();
    this.batchSize = Math.min(options.batchSize ?? DEFAULT_BATCH, CONTRACT_MAX_BATCH);
    this.flushIntervalMs = options.flushIntervalMs ?? DEFAULT_INTERVAL_MS;
    this.maxQueued = options.maxQueued ?? DEFAULT_MAX_QUEUED;
    this.now = options.now ?? Date.now;
    this.queue = [...this.read()];
  }

  /**
   * A store belongs to the host app, so it can fail in ways this package cannot
   * predict. An unreadable queue is an empty one and an unwritable queue lasts only
   * this session; neither is a reason to break the app that called `track`.
   */
  private read(): readonly QueuedEvent[] {
    try {
      return this.store.load();
    } catch (error) {
      this.options.onError?.(error);
      return [];
    }
  }

  private persist(): void {
    try {
      this.store.save(this.queue);
    } catch (error) {
      this.options.onError?.(error);
    }
  }

  /**
   * Whether the learner has opted out. Setting it true drops the queue: keeping events
   * that were gathered before they said no would be keeping data they asked us not to
   * have.
   */
  setOptedOut(optedOut: boolean): void {
    this.optedOut = optedOut;
    if (!optedOut) return;
    this.queue = [];
    this.persist();
    this.cancelTimer();
  }

  /** How many events are waiting. For tests and diagnostics. */
  get pending(): number {
    return this.queue.length;
  }

  /**
   * Records one event. Returns nothing and never rejects: a caller in a tap handler
   * must not have to think about this at all.
   */
  track<K extends AnalyticsEventName>(name: K, payload: AnalyticsPayload<K>): void {
    if (this.optedOut) return;

    this.queue.push({ id: clientUlid(this.now()), name, at: this.now(), payload });

    // Oldest first: recent behaviour is the more useful half of a queue that has to
    // lose something.
    if (this.queue.length > this.maxQueued) {
      this.queue = this.queue.slice(this.queue.length - this.maxQueued);
    }

    this.persist();

    if (this.queue.length >= this.batchSize) {
      void this.flush();
      return;
    }
    this.scheduleFlush();
  }

  /**
   * Sends what is queued. Safe to call at any time and from anywhere; concurrent calls
   * share one in-flight flush rather than sending the same batch twice.
   */
  async flush(): Promise<void> {
    if (this.flushing !== null) return this.flushing;
    this.flushing = this.doFlush().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async doFlush(): Promise<void> {
    this.cancelTimer();
    if (this.optedOut || this.queue.length === 0) return;

    const batch = this.queue.slice(0, CONTRACT_MAX_BATCH);
    const first = batch[0];
    if (first === undefined) return;

    try {
      const result = await this.options.client.call(
        'trackEvents',
        { body: { events: batch } },
        // The batch's own first id is a stable idempotency key, so a retry after a
        // timeout that actually succeeded does not double-count.
        { idempotencyKey: first.id },
      );

      // Dropped from the queue whether the server took them or refused them by name:
      // a refusal is a client bug, and re-sending it forever would hide the bug behind
      // a queue that never drains.
      this.queue = this.queue.slice(batch.length);
      this.persist();

      if (result.rejectedNames.length > 0) {
        this.options.onError?.(
          new Error(`server refused event name(s): ${result.rejectedNames.join(', ')}`),
        );
      }

      // Everything discarded and nothing accepted means the learner has opted out on
      // another device. Stop queueing here too rather than discovering it every flush.
      if (result.accepted === 0 && result.discarded === batch.length) {
        this.setOptedOut(true);
        return;
      }

      if (this.queue.length > 0) this.scheduleFlush();
    } catch (error) {
      // Offline, or the server is unhappy. The batch stays queued and the next flush
      // tries again; nothing is thrown at whoever called `track`.
      this.options.onError?.(error);
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.timer !== null || this.optedOut) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.flushIntervalMs);
    // Node keeps the process alive for a pending timer; a CLI or a test should not
    // hang for 15 seconds because one event was recorded.
    (this.timer as { unref?: () => void }).unref?.();
  }

  private cancelTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }
}
